import React, { useState, useEffect, useContext } from 'react';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';
import { UserContext } from '@contexts/UserContext';

import { isContactApproved } from '@hooks/API/Contact/UseContactsList';
import { useEvent } from '@hooks/API/Event/UseEvent';

import AddBookingEventForm from '@components/EventDrawer/BookingEvent/AddBookingEventForm';
import { BookingEventContextProvider } from '@components/EventDrawer/BookingEvent/BookingEventContext';
import BookingEventDetails from '@components/EventDrawer/BookingEvent/BookingEventDetails/BookingEventDetails';
import EventDrawerDetailsContainer from '@components/EventDrawer/EventDrawerUi/EventDrawerDetailsContainer';
import { EventContainer } from '@components/EventDrawer/EventDrawerUi/EventUI';
import { Loading } from '@components/Gui/Loading';
import SystemMessage from '@components/Utils/SystemMessage';

const viewModeTypes = {
    create: 'create',
    edit: 'edit',
};

const BookingEvent = ({
    afterRemove,
    afterUpdate,
    allDay,
    dateSelected,
    eventInfo,
    filterModel,
    ID,
    onClose,
    onSelectEvent,
    onTouchForm,
    plainMode,
    projectId,
    touchForm,
    viewMode,
}) => {
    const [{ areaContext }] = useStateValue();

    const [eventID, setEventID] = useState(ID);
    const [viewType, setViewType] = useState(viewMode);
    const { data: eventData, isFetching } = useEvent(eventID);
    const { currentUser } = useContext(UserContext);
    const { cachedList } = useContext(ListContext);
    useEffect(() => {
        setEventID(ID);
    }, [ID]);

    const { isMobile, breakpoints } = useLayoutContext();

    const approvedContact = !filterModel ? true : isContactApproved(filterModel);

    if (!approvedContact) {
        return <SystemMessage type="error" text="This contact is pending approval and can't be added to a booking event." />;
    }

    if (viewType === viewModeTypes.create) {
        return (
            <EventContainer noProjectHeader>
                <div className="ant-drawer-header ant-drawer-header-sticky ">
                    <div className="ant-drawer-title">Add Booking Event</div>
                </div>
                <div className="event-content-wrapper">
                    <AddBookingEventForm
                        projectId={projectId}
                        eventInfo={eventInfo}
                        modelId={filterModel}
                        allDay={allDay}
                        touchForm={touchForm}
                        onTouchForm={onTouchForm}
                        dateSelected={dateSelected}
                        onCreateBookingEvent={(eventId) => {
                            setEventID(eventId);
                            setViewType(undefined);
                        }}
                        onCancel={() => {
                            setViewType(undefined);
                            onClose();
                        }}
                        afterUpdate={afterUpdate}
                    />
                </div>
            </EventContainer>
        );
    }

    if (!eventData) {
        return <Loading textBlack />;
    }

    if (eventData.NetworkAgencyCreated !== currentUser.ActiveContext.nwAgId && areaContext.area !== 'accounting') {
        let networkAgencies = [];
        for (const accountingItem of cachedList.accountingGroups.items) {
            networkAgencies = [...networkAgencies, ...accountingItem.NetworkAgencies];
        }
        const networkAgency = networkAgencies.find((agency) => agency.ID === eventData.NetworkAgencyCreated);

        return (
            <div style={{ padding: '6rem 2rem', textAlign: 'center' }}>
                <h2>Please switch to the correct network agency ({networkAgency.Name}) to view this event</h2>
                <p>
                    <a href="/booking/">Go back to booking chart</a>
                </p>
            </div>
        );
    }

    return (
        <BookingEventContextProvider isMobile={isMobile} breakpoint={breakpoints}>
            {plainMode ? (
                <BookingEventDetails
                    plainMode
                    eventData={eventData}
                    filterModel={filterModel}
                    dateSelected={dateSelected}
                    afterUpdate={afterUpdate}
                    afterRemove={afterRemove}
                    onSelectEvent={onSelectEvent}
                />
            ) : (
                <EventDrawerDetailsContainer
                    eventData={{ ...eventData, elType: 'Event', uniqID: `Event-${eventData.ID}` }}
                    filterModel={filterModel}
                    isFetching={isFetching}
                    typeName="event"
                    firstTab={
                        <BookingEventDetails
                            eventData={eventData}
                            filterModel={filterModel}
                            dateSelected={dateSelected}
                            afterUpdate={afterUpdate}
                            afterRemove={afterRemove}
                            onSelectEvent={onSelectEvent}
                        />
                    }
                />
            )}
        </BookingEventContextProvider>
    );
};

export default BookingEvent;
